// pages/Campaigns/Gmail/ReplyModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal as MuiModal, Box, Tooltip, IconButton, Divider, Stack, Button } from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import axiosInstance from "../../../utils/axiosInstance";
import backendURL from '../../../config';

// --- Tiptap Toolbar with Font Awesome Icons ---
const TiptapToolbar = ({ editor }) => {
    const setLink = useCallback(() => {
        if (!editor) return;
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        const finalUrl = (!/^https?:\/\//i.test(url)) ? `https://${url}` : url;
        editor.chain().focus().extendMarkRange('link').setLink({ href: finalUrl }).run();
    }, [editor]);

    if (!editor) return null;

    return (
        <div className="tiptap-toolbar-gmail">
            <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''} title="Bold"><i className="fa fa-bold"></i></button>
            <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''} title="Italic"><i className="fa fa-italic"></i></button>
            <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''} title="Underline"><i className="fa fa-underline"></i></button>
            <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''} title="Strike"><i className="fa fa-strikethrough"></i></button>
            <button type="button" onClick={setLink} className={editor.isActive('link') ? 'is-active' : ''} title="Set Link"><i className="fa fa-link"></i></button>
            <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''} title="Bullet List"><i className="fa fa-list-ul"></i></button>
            <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''} title="Ordered List"><i className="fa fa-list-ol"></i></button>
            <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><i className="fa fa-undo"></i></button>
            <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><i className="fa fa-repeat"></i></button>
        </div>
    );
};
// --- END: Toolbar Component ---

const formatAiReplyForEditor = (text) => {
    if (!text) return '<p></p>';
    const paragraphs = text.trim().split('\n').filter(line => line.trim() !== '').map(line => `<p>${line.trim()}</p>`);
    return paragraphs.join('');
};

export default function ReplyModal({ isOpen, onClose, emailData, onTrain, onUntrain, chatbotId, permission }) {
    const [isSending, setIsSending] = useState(false);
    const [isTraining, setIsTraining] = useState(false);
    const [error, setError] = useState('');
    const [conversation, setConversation] = useState([]);
    const [isConversationLoading, setIsConversationLoading] = useState(false);
    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    const editor = useEditor({
        extensions: [
            StarterKit, 
            Underline, 
            Link.configure({ openOnClick: false, autolink: true }),
        ],
        content: '',
        editorProps: {
            attributes: {
                class: 'form-control',
            },
        },
        editable: permission.canModify,
    });

    useEffect(() => {
        if (editor && emailData?.ai_generated_reply) {
            const formattedHtml = formatAiReplyForEditor(emailData.ai_generated_reply);
            editor.commands.setContent(formattedHtml);
        } else if (editor) {
            editor.commands.setContent('<p></p>');
        }
    }, [editor, emailData]);

    useEffect(() => {
        const fetchConversation = async () => {
            if (!emailData?.thread_id) return;
            setIsConversationLoading(true);
            setError(''); 
            try {
                const response = await axiosInstance.get(`/api/gmail/thread/${emailData.thread_id}`, {
                    params
                });
                setConversation(response.data.data || []);
            } catch (err) {
                setError(err.response?.data?.message || "Failed to load conversation history.");
                setConversation([]);
            } finally {
                setIsConversationLoading(false);
            }
        };

        if (isOpen) {
            fetchConversation();
        } else {
            setConversation([]);
            setIsConversationLoading(false);
        }
    }, [isOpen, emailData?.thread_id]);


    const handleSendReply = async () => {
        if (!editor) return;
        setIsSending(true);
        setError('');
        try {
            await axiosInstance.post(`/api/gmail/send-reply`, {
                thread_id: emailData.thread_id,
                subject: emailData.subject,
                to_address: emailData.from_address,
                body: editor.getHTML(),
                message_db_id: emailData.id,
            }, {
                params
            });
            onClose(true);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to send reply.");
        } finally {
            setIsSending(false);
        }
    };
    
    const handleTrainFromModal = async () => {
        setIsTraining(true);
        await onTrain(emailData);
        setIsTraining(false);
        onClose(true);
    }

    const handleUntrainFromModal = async () => {
        setIsTraining(true);
        await onUntrain(emailData);
        setIsTraining(false);
        onClose(true);
    };

    const modalStyle = {
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '80vw' }, maxWidth: '1400px',
        height: '90vh',
        bgcolor: 'background.paper',
        border: 'none', borderRadius: '8px', boxShadow: 24, p: 3, 
        display: 'flex', flexDirection: 'column', outline: 'none',
    };

    if (!emailData) return null;
    
    const isReplied = emailData.status === 'replied_manual' || emailData.status === 'replied_ai';
    const commonDisabled = isSending || isTraining;

    return (
        <MuiModal open={isOpen} onClose={() => onClose(false)}>
            <Box sx={modalStyle}>
                <h4 className="mb-3 flex-shrink-0">Reply to: {emailData.subject}</h4>
                <div className="row g-3" style={{ flexGrow: 1, overflow: 'hidden' }}>
                    
                    <div className="col-md-6 d-flex flex-column" style={{ height: '100%' }}>
                        <h6 className="flex-shrink-0">Conversation History</h6>
                        <div 
                            className="original-email-viewer flex-grow-1"
                            style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '10px', overflowY: 'auto', background: '#f8f9fa' }}
                        >
                            {isConversationLoading ? (
                                <div className="d-flex justify-content-center align-items-center h-100">
                                    <p>Loading conversation...</p>
                                </div>
                            ) : conversation.length > 0 ? (
                                conversation.map((email, index) => (
                                    <div key={email.id || index} style={{ 
                                        borderBottom: index < conversation.length - 1 ? '1px solid #dee2e6' : 'none', 
                                        paddingBottom: '1rem',
                                        marginBottom: '1rem'
                                    }}>
                                        <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#6c757d' }}>
                                            <div><strong>From:</strong> {email.from_address}</div>
                                            <div><strong>Date:</strong> {new Date(email.received_at).toLocaleString()}</div>
                                        </div>
                                        <div className="email-thread-body" dangerouslySetInnerHTML={{ __html: email.body_html || "<p>No content available.</p>" }}></div>
                                    </div>
                                ))
                            ) : (
                                <p>No conversation history found.</p>
                            )}
                        </div>
                    </div>

                    <div className="col-md-6 d-flex flex-column" style={{ height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
                            <h6>Your Reply (AI Draft)</h6>
                            {isReplied && permission.canModify && (
                                emailData.is_trained ? (
                                    <Tooltip title="Remove this reply from AI's knowledge" arrow>
                                        <span>
                                        <Button size="small" variant="contained" color="error" onClick={handleUntrainFromModal} disabled={commonDisabled}>
                                            <i className="fa fa-unlink me-1"></i> Untrain
                                        </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Train AI with this reply" arrow>
                                        <span>
                                        <Button size="small" variant="contained" color="primary" onClick={handleTrainFromModal} disabled={commonDisabled}>
                                            <i className="fa fa-robot me-1"></i> Train
                                        </Button>
                                        </span>
                                    </Tooltip>
                                )
                            )}
                        </Stack>
                        <div className="flex-grow-1 d-flex flex-column" style={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow:'hidden' }}>
                            <TiptapToolbar editor={editor} />
                            <EditorContent editor={editor} className="flex-grow-1" style={{ overflowY: 'auto' }}/>
                        </div>
                    </div>
                </div>

                {error && <div className="alert alert-danger mt-3 flex-shrink-0">{error}</div>}

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3, flexShrink: 0 }}>
                    <Button variant="outlined" onClick={() => onClose(false)} disabled={commonDisabled}>
                        Cancel
                    </Button>
                    {permission.canModify && (
                        <Button variant="contained" color="success" onClick={handleSendReply} disabled={commonDisabled || !editor?.isEditable}>
                            {isSending ? 'Sending...' : <><i className="fa fa-paper-plane me-1"></i> Send Reply</>}
                        </Button>
                    )}
                </Stack>
                
                <style jsx global>{`
                   .tiptap-toolbar-gmail {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                    align-items: center;
                    padding: 8px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                  }

                  .tiptap-toolbar-gmail button {
                    background: #fff;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    padding: 4px 8px;
                    cursor: pointer;
                    transition: background-color 0.2s, border-color 0.2s;
                  }

                  .tiptap-toolbar-gmail button:hover {
                    background-color: #e9ecef;
                    border-color: #adb5bd;
                  }

                  .tiptap-toolbar-gmail button.is-active {
                    background-color: #0d6efd;
                    color: white;
                    border-color: #0d6efd;
                  }
                  
                  .tiptap-toolbar-gmail button:disabled {
                      opacity: 0.5;
                      cursor: not-allowed;
                  }
                  .ProseMirror { 
                      height: 100%;
                      padding: 10px;
                   }
                  .ProseMirror.form-control {
                      border: none;
                      box-shadow: none;
                  }
                  .ProseMirror:focus { outline: none; }
                  .ProseMirror p { margin-bottom: 0.75rem; }
                  .email-thread-body img {
                    max-width: 100%;
                    height: auto;
                  }
                `}</style>
            </Box>
        </MuiModal>
    );
}